export interface InjectLinkOptions {
    rel: string;
    href: string;
}

/**
 * Appends a `<link>` to `document.head` (e.g. `rel="modulepreload"`).
 */
export const injectLink = ({ rel, href }: InjectLinkOptions): HTMLLinkElement => {
    const link = document.createElement("link");
    link.rel = rel;
    link.href = href;
    document.head.appendChild(link);
    return link;
};

/**
 * Appends a stylesheet `<link rel="stylesheet">` to `document.head`.
 */
export const injectStyle = (href: string): HTMLLinkElement => injectLink({ rel: "stylesheet", href });

export interface InjectScriptOptions {
    /**
     * If true, inserts `<link rel="modulepreload" href="…">` in `document.head` before the script.
     */
    modulePreload?: boolean;
    /** Invoked when the script element fires `load` (before the returned promise resolves). */
    onLoad?: (event: Event) => void;
}

/**
 * Appends a module `<script type="module">` to `document.body`. Resolves after load, rejects on error.
 */
export const injectScript = (src: string, options?: InjectScriptOptions): void => {
    const useModulePreload = options?.modulePreload === true;

    if (useModulePreload) {
        injectLink({ rel: "modulepreload", href: src });
    }

    const script = document.createElement("script");
    script.type = "module";
    script.src = src;

    script.onload = (event: Event) => {
        options?.onLoad?.(event);
    };

    document.body.appendChild(script);
};
